import React from 'react';
import {
  Modal,
  Backdrop,
  Fade,
  Box,
  Typography,
  TextField,
  Button,
  Autocomplete,
  Alert,
} from '@mui/material';
import { LightUserDto } from '@cloudcost/shared-types/users';
import { Department, SystemFormData } from '../../types';

interface EditSystemModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onDelete: () => void;
  users: LightUserDto[];
  departments: Department[];
  formData: SystemFormData;
  setFormData: (data: SystemFormData) => void;
  alert: boolean;
}

export const EditSystemModal: React.FC<EditSystemModalProps> = ({
  open,
  onClose,
  onSubmit,
  onDelete,
  users,
  departments,
  formData,
  setFormData,
  alert,
}) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{
        backdrop: Backdrop,
      }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}>
      <Fade in={open}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            boxShadow: '0 12px 40px rgba(16,35,58,.16)',
            p: 4,
            borderRadius: '14px',
          }}>
          <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
            Edit System
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              id="edit-system-name"
              label="System Name"
              variant="outlined"
              fullWidth
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              inputProps={{ 'data-testid': 'system-edit-name' }}
            />

            <Autocomplete
              disablePortal
              id="combo-box-edit-departments"
              options={departments}
              value={
                departments.find(
                  (row: Department) => row.id === formData.department_id,
                ) ?? null
              }
              getOptionLabel={(option: Department) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Department"
                  variant="outlined"
                  inputProps={{ ...params.inputProps, 'data-testid': 'system-edit-department' }}
                />
              )}
              onChange={(event, value: Department | null) => {
                if (value) {
                  setFormData({
                    ...formData,
                    department_id: value.id,
                  });
                }
              }}
            />

            <Autocomplete
              disablePortal
              id="combo-box-edit-users"
              options={users}
              value={
                users.find(
                  (row: LightUserDto) => row.id === formData.systemowner,
                ) ?? null
              }
              getOptionLabel={(option: LightUserDto) => option.username}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Pick System Owner"
                  variant="outlined"
                  inputProps={{ ...params.inputProps, 'data-testid': 'system-edit-owner' }}
                />
              )}
              onChange={(event, value: LightUserDto | null) => {
                if (value) {
                  setFormData({ ...formData, systemowner: value.id });
                }
              }}
            />

            {alert && (
              <Alert severity="error">
                System needs an owner and department
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                onClick={onSubmit}
                color="primary"
                fullWidth
                data-testid="system-edit-submit">
                Update System
              </Button>
              <Button
                variant="contained"
                onClick={onDelete}
                color="error"
                fullWidth
                data-testid="system-edit-delete">
                Delete System
              </Button>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};